import type { HelpStep } from "newui/components/ui/Help";
import { TOUR_USER_INVITED } from "./tourFixtures";

/**
 * Guided tour steps for "View a user's access" (PRO-730 topic chooser).
 *
 * Flow:
 *   1. Highlight the dummy row's "View access" action → open the read-only
 *      access matrix for it.
 *   2. The modal as a whole.
 *   3. The user's role pills.
 *   4. The "Company-wide" tab (the default matrix view).
 *   5. The "Project & RFx" tab — closes the modal once the tour moves past it.
 *
 * Opens against the dummy `TOUR_USER_INVITED` shell (see
 * `UserListPage.openViewAccessForTour`); `UserAccessMatrixModal` never fires
 * its permissions/modules/assignments queries for a negative (tour) user id.
 */
export interface ViewAccessSteps {
	closeViewAccess: () => void;
	openViewAccess: () => void;
}

export const buildViewAccessSteps = ({
	openViewAccess,
	closeViewAccess,
}: ViewAccessSteps): HelpStep[] => [
	{
		target: `[data-testid="user-action-view-access-${TOUR_USER_INVITED.id}"]`,
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">View a user&apos;s access</h3>
				<p>
					Click the grid icon on any row to see that user&apos;s full, resolved
					access. We&apos;ll open it for you on the next step.
				</p>
			</div>
		),
		placement: "left",
		disableBeacon: true,
		onAfterStep: () => {
			openViewAccess();
		},
	},
	{
		target: '[data-testid="user-access-matrix-modal"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">A read-only view of their access</h3>
				<p>
					This is server-authoritative — it shows exactly what the user can do,
					not what a role name implies.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
		spotlightPadding: 12,
	},
	{
		// PRO-731 2.1 removed the separate role chip list; the roles are now the
		// matrix's own column headers, so this step points at the grid instead of
		// a list that no longer exists.
		target: '[data-testid="user-access-role-columns"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Assigned roles</h3>
				<p>
					Every role contributing to this user&apos;s access gets a column, so you
					can see which role grants what.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="user-access-tab-company"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">All roles</h3>
				<p>
					Module by module, the level each role grants — the default view, and
					what applies everywhere unless a project or RFx scopes it further.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="user-access-tab-scoped"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Project &amp; RFx access</h3>
				<p>
					Any role granted only for specific projects or RFx events shows up on
					this tab instead of the company-wide matrix.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
		onAfterStep: () => {
			closeViewAccess();
		},
	},
];
